import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import type { Conversation, ConversationStatus } from "@zapir/shared";
import { api, type Page } from "@/lib/api";
import { cacheConversations, readConversations } from "@/lib/db";
import { qk } from "@/lib/query";
import { useSession, toast } from "@/lib/store";
import { asApiError } from "@/lib/errors";

export type ConversationFilter = "todas" | "nao_lidas" | "minhas" | "fixadas" | "sem_resposta";

export interface ConversationFilters {
  filtro: ConversationFilter;
  etiqueta?: string;
  assignee?: string;
  status?: ConversationStatus;
  q?: string;
}

export interface ConversationGroups {
  pinned: Conversation[];
  others: Conversation[];
}

export function groupConversations(items: Conversation[]): ConversationGroups {
  const pinned: Conversation[] = [];
  const others: Conversation[] = [];
  for (const item of items) {
    if (item.pinnedAt) pinned.push(item);
    else others.push(item);
  }
  return { pinned, others };
}

export function useConversations(filters: ConversationFilters) {
  const tenantId = useSession((s) => s.tenantId);
  const extra = useMemo(() => JSON.stringify(filters), [filters]);
  const query = useInfiniteQuery({
    queryKey: qk.conversations(tenantId, filters.filtro, extra),
    queryFn: ({ pageParam }) =>
      api.conversations({ ...filters, cursor: pageParam as string | undefined, limit: 30 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) =>
      last.pageInfo.hasMore ? (last.pageInfo.nextCursor ?? undefined) : undefined,
    enabled: Boolean(tenantId),
  });

  const items = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data],
  );

  useEffect(() => {
    if (items.length > 0 && tenantId) void cacheConversations(tenantId, items);
  }, [items, tenantId]);

  return { ...query, items };
}

/** Le a lista em cache quando offline (RF-UX-050/052/EC-UX-011). */
export function useCachedConversations(): { items: Conversation[]; cachedAt: number | null; loading: boolean } {
  const tenantId = useSession((s) => s.tenantId);
  const [state, setState] = useState<{ items: Conversation[]; cachedAt: number | null; loading: boolean }>({
    items: [],
    cachedAt: null,
    loading: true,
  });

  const load = useCallback(async () => {
    if (!tenantId) {
      setState({ items: [], cachedAt: null, loading: false });
      return;
    }
    const cached = await readConversations(tenantId);
    setState({ items: cached?.items ?? [], cachedAt: cached?.cachedAt ?? null, loading: false });
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  return state;
}

/* ---------------- Patch otimista em todas as listas ---------------- */

function patchLists(
  qc: ReturnType<typeof useQueryClient>,
  conversationId: string,
  updater: (c: Conversation) => Conversation,
) {
  type InfiniteConversations = {
    pages: Page<Conversation>[];
    pageParams: unknown[];
  };
  qc.setQueriesData<InfiniteConversations>({ queryKey: ["conversations"] }, (data) => {
    if (!data || !Array.isArray(data.pages)) return data;
    return {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        items: page.items.map((c) => (c.id === conversationId ? updater(c) : c)),
      })),
    };
  });
  qc.setQueryData<Conversation>(qk.conversation(conversationId), (c) => (c ? updater(c) : c));
}

export function usePinConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) => api.setPinned(id, pinned),
    onMutate: async ({ id, pinned }) => {
      const previous = qc.getQueriesData({ queryKey: ["conversations"] });
      patchLists(qc, id, (c) => ({ ...c, pinnedAt: pinned ? new Date().toISOString() : null }));
      return { previous };
    },
    onError: (error, _vars, context) => {
      for (const [key, data] of context?.previous ?? []) qc.setQueryData(key, data);
      const apiError = asApiError(error);
      toast({
        tone: "danger",
        message: apiError?.copy ?? "Não foi possível fixar a conversa.",
        actionLabel: apiError?.code === "PIN_LIMIT_REACHED" ? apiError.action : undefined,
        onAction:
          apiError?.code === "PIN_LIMIT_REACHED"
            ? () => {
                window.location.assign("/conversas?filtro=fixadas");
              }
            : undefined,
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

export function useSetRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, read }: { id: string; read: boolean }) => api.setRead(id, read),
    onMutate: async ({ id, read }) => {
      const previous = qc.getQueriesData({ queryKey: ["conversations"] });
      patchLists(qc, id, (c) => ({ ...c, unreadCount: read ? 0 : Math.max(1, c.unreadCount) }));
      return { previous };
    },
    onError: (_e, _v, context) => {
      for (const [key, data] of context?.previous ?? []) qc.setQueryData(key, data);
    },
  });
}

export function useAssignConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      userId,
      expectedAssigneeId,
    }: {
      id: string;
      userId: string | null;
      expectedAssigneeId?: string | null;
    }) => api.assign(id, userId, expectedAssigneeId),
    onSuccess: (updated) => {
      patchLists(qc, updated.id, () => updated);
      toast({ tone: "success", message: "Conversa atribuída." });
    },
    onError: (error) => {
      const apiError = asApiError(error);
      toast({ tone: "danger", message: apiError?.copy ?? "Não foi possível atribuir." });
      void qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

export function useRenameContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.renameContact(id, name),
    onSuccess: (updated) => {
      patchLists(qc, updated.id, () => updated);
      toast({ tone: "success", message: "Contato renomeado." });
    },
    onError: (error) => {
      const apiError = asApiError(error);
      toast({ tone: "danger", message: apiError?.copy ?? "Não foi possível renomear." });
    },
  });
}

export function useSetConversationLabels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, labelIds }: { id: string; labelIds: string[] }) => api.setLabels(id, labelIds),
    onSuccess: (updated) => {
      patchLists(qc, updated.id, () => updated);
      toast({ tone: "success", message: "Etiquetas atualizadas." });
    },
    onError: (error) => {
      const apiError = asApiError(error);
      toast({ tone: "danger", message: apiError?.copy ?? "Não foi possível etiquetar." });
    },
  });
}

export function useSetConversationStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ConversationStatus }) =>
      api.setStatus({ conversationId: id, status }),
    onSuccess: (updated) => {
      patchLists(qc, updated.id, () => updated);
      toast({ tone: "success", message: "Status atualizado." });
    },
    onError: (error) => {
      const apiError = asApiError(error);
      toast({ tone: "danger", message: apiError?.copy ?? "Não foi possível mudar o status." });
    },
  });
}
